import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBusinessContext } from '../contexts/BusinessContext';
import { usePermissions } from '../contexts/PermissionContext';
import { ChevronRight, Download, MapPin, ArrowRight } from 'lucide-react';
import { useDateFormatter } from '../utils/dateUtils';
import { useEnhancedDashboardData } from '../hooks/useEnhancedDashboardData';
// Utility functions are imported by card components
import WealthGapCalculatorModal from '../components/dashboard/WealthGapCalculatorModalEnhanced';
import WealthGapDetailsModal from '../components/dashboard/WealthGapDetailsModalEnhanced';
import ProfitGapDetailsModal from '../components/dashboard/ProfitGapDetailsModal';
import ValueGapDetailsModal from '../components/dashboard/ValueGapDetailsModal';
import GapUnderstandingModal from '../components/dashboard/GapUnderstandingModal';
import ValuationProgressionModal from '../components/dashboard/ValuationProgressionModal';
import ProgressionFlowModal from '../components/ProgressionFlowModal';
import ClientProfileCard from '../components/dashboard/ClientProfileCard';
import BusinessProfileCard from '../components/dashboard/BusinessProfileCard';
import AdvisoryTeamCard from '../components/dashboard/AdvisoryTeamCard';
import BusinessValuationCard from '../components/dashboard/BusinessValuationCard';
import GapAssessmentsCard from '../components/dashboard/GapAssessmentsCard';
import EnvisioningCard from '../components/dashboard/EnvisioningCard';
import TaskManagerCard from '../components/dashboard/TaskManagerCard';
import WealthGapCard from '../components/dashboard/WealthGapCard';
import ProfitGapCard from '../components/dashboard/ProfitGapCard';
import ValueGapCard from '../components/dashboard/ValueGapCard';
import ViewerBanner from '../components/ViewerBanner';
import PermissionGate from '../components/PermissionGate';

const EnhancedDashboard = () => {
  const { user } = useAuth();
  const { currentBusinessId, isAdvisor } = useBusinessContext();
  const { isViewer } = usePermissions();
  const { formatDate } = useDateFormatter();

  const currentUser = user;

  const [showProgressionModal, setShowProgressionModal] = useState(false);
  const [showProgressionFlow, setShowProgressionFlow] = useState(false);
  const [profileCardsExpanded, setProfileCardsExpanded] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  // Use custom hook for all data management
  const {
    latestValuation,
    businessProfile,
    assessment,
    personalAssessment,
    wealthGap,
    profitGap,
    valueGap,
    taskStats,
    loading,
    showWealthGapCalculator,
    setShowWealthGapCalculator,
    wealthGapMethod,
    setWealthGapMethod,
    wealthGoalAmount,
    setWealthGoalAmount,
    monthlyCashNeed,
    setMonthlyCashNeed,
    yearsOfIncome,
    setYearsOfIncome,
    inflationRate,
    setInflationRate,
    returnRate,
    setReturnRate,
    liquidAssets,
    setLiquidAssets,
    retirementAccounts,
    setRetirementAccounts,
    realEstateEquity,
    setRealEstateEquity,
    otherInvestments,
    setOtherInvestments,
    totalLiabilities,
    setTotalLiabilities,
    savingWealthGap,
    outsideIncomeMonthly,
    setOutsideIncomeMonthly,
    outsideIncomeAnnual,
    setOutsideIncomeAnnual,
    outsideIncomeAnnualIncrease,
    setOutsideIncomeAnnualIncrease,
    currentAge,
    setCurrentAge,
    retirementAge,
    setRetirementAge,
    lifeExpectancy,
    setLifeExpectancy,
    taxRateOnSale,
    setTaxRateOnSale,
    ownershipPercentage,
    setOwnershipPercentage,
    desiredSalePrice,
    setDesiredSalePrice,
    businessGrowthRate,
    setBusinessGrowthRate,
    exitYear,
    setExitYear,
    inheritanceAmount,
    setInheritanceAmount,
    leisureItemsAmount,
    setLeisureItemsAmount,
    lifestyleChangePercentage,
    setLifestyleChangePercentage,
    spendDecreaseAfterRetirement,
    setSpendDecreaseAfterRetirement,
    liquidAssetsReturnRate,
    setLiquidAssetsReturnRate,
    retirementAccountsReturnRate,
    setRetirementAccountsReturnRate,
    realEstateReturnRate,
    setRealEstateReturnRate,
    otherInvestmentsReturnRate,
    setOtherInvestmentsReturnRate,
    showWealthGapDetails,
    setShowWealthGapDetails,
    showProfitGapDetails,
    setShowProfitGapDetails,
    showValueGapDetails,
    setShowValueGapDetails,
    showGapModal,
    setShowGapModal,
    handleSaveWealthGap,
    handleRecalculateProfitGap,
    handleRecalculateValueGap
  } = useEnhancedDashboardData(isAdvisor, currentBusinessId);

  // Handler for downloading dashboard PDF
  const handleDownloadPDF = async () => {
    if (!currentBusinessId) {
      alert('No business selected');
      return;
    }

    setDownloadingPDF(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/pdf/dashboard/${currentBusinessId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Business-Id': currentBusinessId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition && contentDisposition.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : 'Dashboard_Report.pdf';

      // Download the PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Viewer Banner */}
      <ViewerBanner />

      {/* Header */}
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
            Welcome back, {currentUser?.full_name?.split(' ')[0] || currentUser?.user_metadata?.full_name?.split(' ')[0] || 'there'}!
          </h1>
          <p className="text-sm sm:text-base text-gray-600">Your comprehensive exit planning dashboard</p>
        </div>
        <button
          onClick={handleDownloadPDF}
          disabled={downloadingPDF}
          className="flex items-center gap-2 px-4 sm:px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md w-full sm:w-auto min-h-[44px]"
        >
          <Download size={20} />
          {downloadingPDF ? 'Generating PDF...' : 'Download PDF'}
        </button>
      </div>

      {/* Progression Flow Button */}
      <div className="mb-6 sm:mb-8">
        <button
          onClick={() => setShowProgressionFlow(true)}
          className="w-full bg-gradient-to-r from-rose-600 to-yellow-400 hover:from-rose-700 hover:to-yellow-400 text-white rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-200 group min-h-[44px]"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 bg-white bg-opacity-20 rounded-lg">
                <MapPin className="h-6 w-6 sm:h-8 sm:w-8" />
              </div>
              <div className="text-left">
                <h3 className="text-base sm:text-xl font-bold mb-1">Your Exit Planning Roadmap</h3>
                <p className="text-blue-100 text-xs sm:text-sm">
                  Follow our step-by-step guide to prepare for a successful business exit
                </p>
              </div>
            </div>
            <ArrowRight className="hidden sm:block h-10 w-10 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
          </div>
        </button>
      </div>

      {/* ROW 1 - Client and Business Profile Summary */}
      <div className="space-y-4 sm:space-y-6">
        {/* Step 1 Header */}
        <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center w-14 sm:w-16 h-7 sm:h-8 bg-black text-white font-bold text-xs rounded-full">
              Step 1
            </span>
          </div>
          <div className="flex-1 h-0.5 bg-gradient-to-r from-black to-transparent"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {/* Client Profile Summary */}
          <ClientProfileCard
            businessProfile={businessProfile}
            isExpanded={profileCardsExpanded}
            onToggle={() => setProfileCardsExpanded(!profileCardsExpanded)}
          />

          {/* Business/Exit Profile Summary */}
          <BusinessProfileCard
            businessProfile={businessProfile}
            isExpanded={profileCardsExpanded}
            onToggle={() => setProfileCardsExpanded(!profileCardsExpanded)}
          />

          {/* Advisory Team Card */}
          <AdvisoryTeamCard
            businessProfile={businessProfile}
            isExpanded={profileCardsExpanded}
            onToggle={() => setProfileCardsExpanded(!profileCardsExpanded)}
          />
        </div>
        <div className="flex-1 h-0.5 bg-gradient-to-r from-black to-transparent"></div>

        {/* ROW 2 - Gap Assessments, Latest Valuation, Value Gap */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {/* Gap Assessments - Steps 2 & 3 */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-16 h-8 bg-blue-600 text-white font-bold text-xs rounded-full">
                  Step 2
                </span>
                <h3 className="text-xs font-bold text-gray-500">& </h3>
                <span className="flex items-center justify-center w-16 h-8 bg-green-600 text-white font-bold text-xs rounded-full">
                  Step 3
                </span>
              </div>
            </div>
            <GapAssessmentsCard
              assessment={assessment}
              personalAssessment={personalAssessment}
            />
          </div>

          {/* Latest Opinion of Value - Step 4 */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-16 h-8 bg-indigo-600 text-white font-bold text-xs rounded-full">
                  Step 4
                </span>
              </div>
            </div>
            <BusinessValuationCard
              latestValuation={latestValuation}
              formatDate={formatDate}
            />
          </div>

          {/* Value Gap Widget - Step 5 */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-16 h-8 bg-emerald-600 text-white font-bold text-xs rounded-full">
                 Step 5
                </span>
              </div>
            </div>
            <ValueGapCard
              valueGap={valueGap}
              setShowValueGapDetails={setShowValueGapDetails}
              handleRecalculateValueGap={handleRecalculateValueGap}
              isViewer={isViewer}
            />
          </div>
        </div>
        <div className="flex-1 h-0.5 bg-gradient-to-r from-black to-transparent"></div>

        {/* ROW 3 - Profit Gap, Wealth Gap, Gap Understanding Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {/* Profit Gap Widget - Step 6 */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-16 h-8 bg-purple-600 text-white font-bold text-xs rounded-full">
                 Step 6
                </span>
              </div>
            </div>
            <ProfitGapCard
              profitGap={profitGap}
              setShowProfitGapDetails={setShowProfitGapDetails}
              handleRecalculateProfitGap={handleRecalculateProfitGap}
              isViewer={isViewer}
            />
          </div>

          {/* Wealth Gap Widget - Step 7 */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-16 h-8 bg-orange-600 text-white font-bold text-xs rounded-full">
                 Step 7
                </span>
              </div>
            </div>
            <WealthGapCard
              wealthGap={wealthGap}
              setShowWealthGapDetails={setShowWealthGapDetails}
              setShowWealthGapCalculator={setShowWealthGapCalculator}
              isViewer={isViewer}
            />
          </div>

          {/* Gap Analysis Understanding Buttons - Stacked */}
          <div className="flex flex-col gap-3 sm:gap-4 justify-center">
            {/* Understanding Your Business Exit Gaps Button */}
            <button
              onClick={() => setShowGapModal(true)}
              className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl shadow-lg p-4 sm:p-6 border border-gray-200 hover:border-blue-400 hover:shadow-xl transition-all group text-left min-h-[44px]"
            >
              <div className="flex items-center justify-between gap-2">
                <div>
                  <h2 className="text-sm sm:text-base font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">Understanding Your Business Exit Gaps</h2>
                  <p className="text-gray-600 text-xs sm:text-sm">Learn about the three gaps and how to improve them</p>
                </div>
                <ChevronRight size={20} className="text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0" />
              </div>
            </button>

            {/* Valuation Progression Chart Button */}
            <button
              onClick={() => setShowProgressionModal(true)}
              className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl shadow-lg p-4 sm:p-6 border border-gray-200 hover:border-emerald-400 hover:shadow-xl transition-all group text-left min-h-[44px]"
            >
              <div className="flex items-center justify-between gap-2">
                <div>
                  <h2 className="text-sm sm:text-base font-bold text-gray-900 mb-1 group-hover:text-emerald-600 transition-colors">Your Valuation Journey</h2>
                  <p className="text-gray-600 text-xs sm:text-sm">Visualize your path to exit readiness</p>
                </div>
                <ChevronRight size={20} className="text-gray-400 group-hover:text-emerald-600 transition-colors flex-shrink-0" />
              </div>
            </button>
          </div>
        </div>
        <div className="flex-1 h-0.5 bg-gradient-to-r from-black to-transparent"></div>

        {/* ROW 4 - Envisioning and Task Manager */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {/* Envisioning - Steps 8 & 9 */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-16 h-8 bg-rose-600 text-white font-bold text-xs rounded-full">
                  Step 8
                </span>
                <h3 className="text-xs font-bold text-gray-500">& </h3>
                <span className="flex items-center justify-center w-16 h-8 bg-pink-600 text-white font-bold text-xs rounded-full">
                  Step 9
                </span>
              </div>
            </div>
            <EnvisioningCard />
          </div>

          {/* Task Manager Summary - Step 10 */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-16 h-8 bg-cyan-600 text-white font-bold text-xs rounded-full">
                 Step 10
                </span>
              </div>
            </div>
            <TaskManagerCard taskStats={taskStats} />
          </div>
        </div>
      </div>

            {/* Wealth Gap Calculator Modal */}
      <WealthGapCalculatorModal
        show={showWealthGapCalculator}
        onClose={() => setShowWealthGapCalculator(false)}
        wealthGap={wealthGap}
        businessProfile={businessProfile}
        profitGap={profitGap}
        valueGap={valueGap}
        onSave={handleSaveWealthGap}
        saving={savingWealthGap}
        wealthGapMethod={wealthGapMethod}
        setWealthGapMethod={setWealthGapMethod}
        wealthGoalAmount={wealthGoalAmount}
        setWealthGoalAmount={setWealthGoalAmount}
        monthlyCashNeed={monthlyCashNeed}
        setMonthlyCashNeed={setMonthlyCashNeed}
        yearsOfIncome={yearsOfIncome}
        setYearsOfIncome={setYearsOfIncome}
        inflationRate={inflationRate}
        setInflationRate={setInflationRate}
        returnRate={returnRate}
        setReturnRate={setReturnRate}
        liquidAssets={liquidAssets}
        setLiquidAssets={setLiquidAssets}
        retirementAccounts={retirementAccounts}
        setRetirementAccounts={setRetirementAccounts}
        realEstateEquity={realEstateEquity}
        setRealEstateEquity={setRealEstateEquity}
        otherInvestments={otherInvestments}
        setOtherInvestments={setOtherInvestments}
        totalLiabilities={totalLiabilities}
        setTotalLiabilities={setTotalLiabilities}
        outsideIncomeMonthly={outsideIncomeMonthly}
        setOutsideIncomeMonthly={setOutsideIncomeMonthly}
        outsideIncomeAnnual={outsideIncomeAnnual}
        setOutsideIncomeAnnual={setOutsideIncomeAnnual}
        outsideIncomeAnnualIncrease={outsideIncomeAnnualIncrease}
        setOutsideIncomeAnnualIncrease={setOutsideIncomeAnnualIncrease}
        currentAge={currentAge}
        setCurrentAge={setCurrentAge}
        retirementAge={retirementAge}
        setRetirementAge={setRetirementAge}
        lifeExpectancy={lifeExpectancy}
        setLifeExpectancy={setLifeExpectancy}
        taxRateOnSale={taxRateOnSale}
        setTaxRateOnSale={setTaxRateOnSale}
        ownershipPercentage={ownershipPercentage}
        setOwnershipPercentage={setOwnershipPercentage}
        desiredSalePrice={desiredSalePrice}
        setDesiredSalePrice={setDesiredSalePrice}
        businessGrowthRate={businessGrowthRate}
        setBusinessGrowthRate={setBusinessGrowthRate}
        exitYear={exitYear}
        setExitYear={setExitYear}
        inheritanceAmount={inheritanceAmount}
        setInheritanceAmount={setInheritanceAmount}
        leisureItemsAmount={leisureItemsAmount}
        setLeisureItemsAmount={setLeisureItemsAmount}
        lifestyleChangePercentage={lifestyleChangePercentage}
        setLifestyleChangePercentage={setLifestyleChangePercentage}
        spendDecreaseAfterRetirement={spendDecreaseAfterRetirement}
        setSpendDecreaseAfterRetirement={setSpendDecreaseAfterRetirement}
        liquidAssetsReturnRate={liquidAssetsReturnRate}
        setLiquidAssetsReturnRate={setLiquidAssetsReturnRate}
        retirementAccountsReturnRate={retirementAccountsReturnRate}
        setRetirementAccountsReturnRate={setRetirementAccountsReturnRate}
        realEstateReturnRate={realEstateReturnRate}
        setRealEstateReturnRate={setRealEstateReturnRate}
        otherInvestmentsReturnRate={otherInvestmentsReturnRate}
        setOtherInvestmentsReturnRate={setOtherInvestmentsReturnRate}
      />

            {/* Wealth Gap Details Modal */}
      <WealthGapDetailsModal
        show={showWealthGapDetails}
        onClose={() => setShowWealthGapDetails(false)}
        wealthGap={wealthGap}
      />

            {/* Profit Gap Details Modal */}
      <ProfitGapDetailsModal
        show={showProfitGapDetails}
        onClose={() => setShowProfitGapDetails(false)}
        profitGap={profitGap}
      />

            {/* Value Gap Details Modal */}
      <ValueGapDetailsModal
        show={showValueGapDetails}
        onClose={() => setShowValueGapDetails(false)}
        valueGap={valueGap}
      />

            {/* Gap Understanding Modal */}
      <GapUnderstandingModal
        show={showGapModal}
        onClose={() => setShowGapModal(false)}
      />

            {/* Valuation Progression Modal */}
      <ValuationProgressionModal
        show={showProgressionModal}
        onClose={() => setShowProgressionModal(false)}
        valueGap={valueGap}
        profitGap={profitGap}
      />

      {/* Progression Flow Modal */}
      <ProgressionFlowModal
        isOpen={showProgressionFlow}
        onClose={() => setShowProgressionFlow(false)}
      />
        </div>
  );
};

export default EnhancedDashboard;
